/**
 * Row selection permalink helpers.
 *
 * The selected briefing row lives in `?row=<id>` so that refreshing or sharing
 * the URL preserves which row the user was working with. Unlike the asset
 * permalink the row param is scoped to the Published tab — it has no meaning on
 * Staged, so it is stripped when the tab isn't Published.
 */

export const ROW_PARAM = 'row';

/**
 * Read the selected row id from a query string.
 *
 * Returns null for missing, empty, or whitespace-only values so callers never
 * end up with a non-null id that can't match anything.
 */
export function readRowIdFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get(ROW_PARAM);
  return value && value.trim() ? value : null;
}

/**
 * Return a new URL string with the row param set, removed, or left alone.
 */
export function withSelectedRow(href: string, rowId: string | null): string {
  const url = new URL(href);
  if (rowId) {
    url.searchParams.set(ROW_PARAM, rowId);
  } else {
    url.searchParams.delete(ROW_PARAM);
  }
  return url.toString();
}

/**
 * Strip the row param from a URL string. Used when switching away from the
 * Published tab so a stale `?row=` doesn't linger.
 */
export function stripRowParam(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(ROW_PARAM);
  return url.toString();
}
